import type { AuthUser } from "../../hooks/useAuth";
import type { DiskUsage } from "../../hooks/useClawMetrics";
import type { OpenClaw } from "../../types/openclaw";
import type { DashboardInstance, DashboardOwner, DashboardTier } from "./mock";

// The server sends tier_definition.display_name alongside the tier code;
// prefer it, falling back to the title-cased code (stale cached payloads,
// or a code with no definition row). "explore" is the provisioning default.
export const tierLabelOf = (claw: Pick<OpenClaw, "tier" | "tierDisplayName">): DashboardTier => {
  const displayName = claw.tierDisplayName?.trim();
  if (displayName !== undefined && displayName.length > 0) return displayName;
  return normalizeTier(claw.tier);
};

export const normalizeTier = (raw: string | null | undefined): DashboardTier => {
  if (raw == null || raw.length === 0) return "Explore";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

export const daysSince = (iso: string, now: number = Date.now()): number => {
  const start = Date.parse(iso);
  if (Number.isNaN(start)) return 0;
  const diff = now - start;
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};

export const adaptInstance = (claw: OpenClaw): DashboardInstance => ({
  id: claw.tenantId,
  name: claw.name,
  agentName: claw.name,
  status: claw.status === "RUNNING" ? "running" : "provisioning",
  tier: tierLabelOf(claw),
  uptimeDays: daysSince(claw.createdAt),
  coachingSessionsRemaining: claw.coachingSessionsRemaining,
  unlimitedEntitlements: claw.unlimitedEntitlements,
  locked: claw.locked,
  role: claw.role,
  sharingEnabled: claw.sharingEnabled,
  openrouterKeyConfigurable: claw.openrouterKeyConfigurable === true,
  chatgptSelfServeEnabled: claw.chatgptSelfServeEnabled === true,
  chatgptLinked: claw.chatgptLinked === true,
  aiMode: claw.aiMode ?? "openrouter",
  chatgptAccount: claw.chatgptAccount ?? null,
});

export const withDisk = (
  instance: DashboardInstance,
  disk: DiskUsage | null,
): DashboardInstance =>
  disk === null
    ? instance
    : { ...instance, diskUsedGb: disk.usedGb, diskTotalGb: disk.totalGb };

const titleCase = (segment: string): string =>
  segment.length === 0 ? segment : segment.charAt(0).toUpperCase() + segment.slice(1);

const splitNameFromEmail = (
  email: string,
): { firstName: string; lastName: string } => {
  const local = email.split("@")[0] ?? "";
  if (local.length === 0) return { firstName: "ZeroToClaw", lastName: "User" };
  const parts = local.split(/[._-]+/).filter((p) => p.length > 0);
  if (parts.length === 0) return { firstName: titleCase(local), lastName: "" };
  if (parts.length === 1) return { firstName: titleCase(parts[0]!), lastName: "" };
  return {
    firstName: titleCase(parts[0]!),
    lastName: titleCase(parts[parts.length - 1]!),
  };
};

export const adaptOwner = (user: AuthUser): DashboardOwner => {
  const name = user.displayName?.trim();
  const parts = name?.split(/\s+/);
  const { firstName, lastName } = parts?.length
    ? { firstName: parts[0]!, lastName: parts.slice(1).join(" ") }
    : splitNameFromEmail(user.email);
  return {
    firstName,
    lastName,
    email: user.email,
    bookCallUrl: user.bookOnboardingCallUrl ?? "",
  };
};
